import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AlertController, ToastController } from '@ionic/angular';
import { NaploService } from '../naplo.service';
import { tablazatBeolvas } from '../tablazat-beolvas';

// NAPLO – TÁRSASHÁZ: albetétek (lakók) törzse, tulajdoni hányad, havi közös költség kiírása
// és RLB vevőszámla-export. A bizonylatszám az albetét száma + a hónap – ezen hivatkozva
// egyenlíthető ki később a banki kivonatból.
@Component({
  selector: 'app-naplo-tarsashaz',
  templateUrl: './tarsashaz.component.html',
  styleUrls: ['../naplo-shared.scss', './tarsashaz.component.scss'],
})
export class TarsashazComponent implements OnInit {
  private readonly base = '/api/naplo-tarsashaz';
  get aktiv() { return this.naplo.aktiv; }

  albetetek: any[] = [];
  osszHanyad = 0;
  szerk: any = null;                 // szerkesztett/új albetét
  importFut = false;
  rlbPartnerek: string[] = [];       // az RLB partnertörzsből (párosításhoz)
  // Egységár-törzs: a vízórás és a vízóra nélküli albetét díja eltér, ezért az árat nem
  // havonta gépeljük, hanem kategóriánként tartjuk nyilván, érvényességi kezdettel.
  dijak: any[] = [];
  kategoriak: any[] = [];
  dijSzerk: any = null;              // épp felvitt VAGY módosított egységár

  ev = new Date().getFullYear();
  honap = new Date().getMonth() + 1;
  kiiras: any = { szamitas: 'osszeg', vetites: 'terulet', osszeg: null, egysegar: null, megnevezes: '', vevo_szamla: '311', bevetel_szamla: '9', afa_kod: '' };
  tetelek: any[] = [];
  osszesen = 0;
  hianyzoAlap = 0;              // hány arányos albetétnél 0 a vetítési alap
  ismetlodoJeloles = 0;         // hány albetét megnevezése nem egyedi (bizonylatszám!)
  szamitFut = false;
  exportFut = false;

  constructor(private naplo: NaploService, private http: HttpClient,
              private toast: ToastController, private alert: AlertController) {}

  private get<T = any>(p: string) { return firstValueFrom(this.http.get<T>(this.base + p)); }
  private post<T = any>(p: string, b: any) { return firstValueFrom(this.http.post<T>(this.base + p, b)); }
  private put<T = any>(p: string, b: any) { return firstValueFrom(this.http.put<T>(this.base + p, b)); }
  private del<T = any>(p: string) { return firstValueFrom(this.http.delete<T>(this.base + p)); }

  async ngOnInit() {
    await this.naplo.init();
    if (this.aktiv.ugyfelId) await this.betolt();
  }

  async betolt() {
    await Promise.all([this.albetetekBetolt(), this.partnerekBetolt(), this.dijakBetolt(), this.kiirasBetolt()]);
  }

  async albetetekBetolt() {
    try {
      const r: any = await this.get(`/${this.aktiv.ugyfelId}/albetetek`);
      this.albetetek = r?.albetetek || [];
      this.osszHanyad = r?.ossz_hanyad || 0;
    } catch { this.albetetek = []; }
  }

  // Az RLB partnertörzsből kínáljuk a neveket, hogy a lakó ugyanazzal a névvel menjen a
  // számlára, amivel az RLB-ben szerepel (ugyanaz a törzs, amit a bank-modul importál).
  async partnerekBetolt() {
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/naplo-bank/${this.aktiv.ugyfelId}/rlb-nevlistak`));
      this.rlbPartnerek = r?.torzs || [];
    } catch { this.rlbPartnerek = []; }
  }

  async dijakBetolt() {
    try {
      const r: any = await this.get(`/${this.aktiv.ugyfelId}/dijak`);
      this.dijak = r?.dijak || [];
      this.kategoriak = r?.kategoriak || [];
    } catch { this.dijak = []; this.kategoriak = []; }
  }

  ujDijSor() {
    const ma = new Date();
    this.dijSzerk = { kategoria: this.kategoriak[0]?.kategoria || 'vizoras', nev: this.kategoriak[0]?.nev || '',
      egysegar: null, ervenyes_tol: `${ma.getFullYear()}-01-01` };
  }

  // Meglévő sor módosítása ugyanazon az űrlapon. Enélkül az egységárat csak úgy lehetett
  // átírni, hogy pontosan ugyanazzal az érvényességi kezdettel vettük fel újra.
  dijModosit(d: any) {
    this.dijSzerk = { id: d.id, kategoria: d.kategoria, nev: d.nev || this.kategoriaNev(d.kategoria),
      egysegar: Number(d.egysegar) || 0, ervenyes_tol: this.nap(d.ervenyes_tol) };
    // A szerkesztő a táblázat alatt van; ha a havi kiírásból nyitották, oda kell görgetni.
    setTimeout(() => document.getElementById('egysegar-szerkeszto')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  }

  // A DATE mező Date-ként érkezik, az ISO-alak pedig UTC-re konvertálva egy napot csúszna,
  // ezért a HELYI dátumrészekből rakjuk össze az input értékét.
  private nap(ertek: any) {
    const d = ertek instanceof Date ? ertek : new Date(ertek);
    if (isNaN(d.getTime())) return String(ertek || '').slice(0, 10);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  // A választott hónapban ÉRVÉNYES díjak kategóriánként – ugyanaz a szabály, mint a
  // kiszámításnál: a legutolsó olyan sor, aminek a kezdete még a hónap végére esik.
  get ervenyesDijak(): any[] {
    const hatar = this.nap(new Date(this.ev, this.honap, 0));
    const terkep = new Map<string, any>();
    [...this.dijak]
      .sort((a, b) => this.nap(a.ervenyes_tol).localeCompare(this.nap(b.ervenyes_tol)))
      .forEach((d) => { if (this.nap(d.ervenyes_tol) <= hatar) terkep.set(d.kategoria, d); });
    return [...terkep.values()];
  }

  // A kategória váltásakor a NEVET is át kell állítani: enélkül a sor a kategóriától
  // függetlenül az elsőként felkínált néven („Vízórás") mentődött, és a lista is azt mutatta.
  dijKategoriaValt(kategoria: string) {
    this.dijSzerk.kategoria = kategoria;
    this.dijSzerk.nev = this.kategoriaNev(kategoria);
  }

  async dijMent(d: any) {
    try {
      if (d.id) await this.put(`/${this.aktiv.ugyfelId}/dij/${d.id}`, d);
      else await this.post(`/${this.aktiv.ugyfelId}/dij`, d);
      this.dijSzerk = null;
      await this.dijakBetolt(); await this.kiirasBetolt();
      this.ok(d.id ? 'Egységár módosítva.' : 'Egységár mentve.');
    } catch (e: any) { this.hiba(e?.error?.error || 'A mentés nem sikerült.'); }
  }

  async dijTorol(d: any) {
    try {
      await this.del(`/${this.aktiv.ugyfelId}/dij/${d.id}`);
      await this.dijakBetolt(); await this.kiirasBetolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'A törlés nem sikerült.'); }
  }

  kategoriaNev(kulcs: string) { return this.kategoriak.find((k) => k.kategoria === kulcs)?.nev || kulcs || '—'; }

  async kiirasBetolt() {
    try {
      const r: any = await this.get(`/${this.aktiv.ugyfelId}/kiiras/${this.ev}/${this.honap}`);
      if (r?.beallitas) {
        this.kiiras = {
          szamitas: r.beallitas.szamitas || 'osszeg',
          vetites: r.beallitas.vetites || 'terulet',
          osszeg: r.beallitas.osszeg, egysegar: r.beallitas.egysegar,
          megnevezes: r.beallitas.megnevezes || '', vevo_szamla: r.beallitas.vevo_szamla || '311',
          bevetel_szamla: r.beallitas.bevetel_szamla || '9', afa_kod: r.beallitas.afa_kod || '',
        };
      }
      this.tetelek = r?.tetelek || [];
      this.osszesen = r?.osszesen || 0;
      this.hianyzoAlap = r?.hianyzo_alap || 0;
      this.ismetlodoJeloles = r?.ismetlodo_jeloles || 0;
    } catch { this.tetelek = []; this.osszesen = 0; this.hianyzoAlap = 0; this.ismetlodoJeloles = 0; }
  }

  /* ── albetétek ────────────────────────────────────────────────────────────── */

  ujAlbetet() { this.szerk = { albetet: '', megnevezes: '', tulajdonos: '', rlb_partner: '', hanyad: '', terulet: '', fix_koltseg: '', aktiv: true, dij_kategoria: 'vizoras' }; }
  szerkeszt(a: any) { this.szerk = { ...a, aktiv: a.aktiv !== 0, dij_kategoria: a.dij_kategoria || 'alap' }; }

  async albetetMent() {
    try {
      await this.post(`/${this.aktiv.ugyfelId}/albetet`, this.szerk);
      this.szerk = null;
      await this.albetetekBetolt();
      await this.kiirasBetolt();
      this.ok('Albetét mentve.');
    } catch (e: any) { this.hiba(e?.error?.error || 'A mentés nem sikerült.'); }
  }

  async albetetTorol(a: any) {
    const al = await this.alert.create({
      header: 'Albetét törlése', message: `${a.albetet} – ${a.tulajdonos || ''}`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive' }],
    });
    await al.present();
    if ((await al.onDidDismiss()).role !== 'destructive') return;
    try {
      await this.del(`/${this.aktiv.ugyfelId}/albetet/${a.id}`);
      await this.albetetekBetolt(); await this.kiirasBetolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'A törlés nem sikerült.'); }
  }

  // Lakólista betöltése táblázatból. A SheetJS XLS/XLSX/CSV/DBF-et is olvas – nem szűkítjük
  // a fájlválasztót, mert a társasházi listák a legkülönfélébb formában érkeznek.
  async albetetImport(ev: any) {
    const file = ev?.target?.files?.[0];
    if (!file) return;
    this.importFut = true;
    try {
      // DBF-nél a nyers fájl megy fel (a böngésző olvasója nem ismeri fel), minden mást itt
      // parse-olunk – lásd tablazat-beolvas.ts.
      const { sorok, dbf } = await tablazatBeolvas(file);
      if (!dbf && !sorok.length) throw new Error('a fájlban nem találtam sorokat');
      const r: any = await this.post(`/${this.aktiv.ugyfelId}/albetet-import`, dbf ? { dbf } : { sorok });
      await this.albetetekBetolt(); await this.kiirasBetolt();
      this.ok(`${r?.db || 0} albetét betöltve (${file.name}).`);
    } catch (e: any) {
      this.hiba('Az import nem sikerült: ' + (e?.error?.error || e?.message || e));
    } finally { this.importFut = false; if (ev?.target) ev.target.value = ''; }
  }

  arany(a: any) { return this.osszHanyad > 0 ? (Number(a.hanyad || 0) / this.osszHanyad) * 100 : 0; }

  /*  A LAKÓLISTA OSZLOPAI – egy helyen, mert három dolognak kell egyeznie: a minta-sablon
   *  fejlécének, az export fejlécének és annak, amit az import felismer (naplo-tarsashaz.js).
   *  Aki az exportált fájlt szerkeszti és visszatölti, pontosan ugyanazt kapja vissza. */
  private static readonly OSZLOPOK = ['Albetét', 'Megnevezés', 'Tulajdonos', 'RLB partner', 'Partnerkód',
    'Tulajdoni hányad', 'Terület', 'Díjkategória', 'Fix költség', 'Aktív', 'Megjegyzés'];

  // Pontosvesszős CSV: a mezőt csak akkor idézőjelezzük, ha muszáj (elválasztó, idézőjel, sortörés).
  private csvMezo(ertek: any): string {
    const t = ertek == null ? '' : String(ertek);
    return /[";\r\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t;
  }
  // Tizedesvessző: a magyar Excel ezt várja, az import pedig a vesszőt is érti.
  private csvSzam(ertek: any): string {
    if (ertek == null || ertek === '') return '';
    const n = Number(ertek);
    return Number.isFinite(n) ? String(n).replace('.', ',') : String(ertek);
  }
  private csvLetolt(fajlnev: string, sorok: any[][]) {
    const szoveg = sorok.map((sor) => sor.map((m) => this.csvMezo(m)).join(';')).join('\r\n') + '\r\n';
    // BOM, hogy az Excel felismerje az UTF-8-at (ékezetek miatt).
    const blob = new Blob(['\ufeff' + szoveg], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = fajlnev;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(a.href);
  }

  // Minta-sablon: pontosan azokkal a fejlécekkel, amiket az import felismer. Excelben megnyitva
  // kitölthető, és úgy tölthető vissza – így nem kell találgatni, milyen oszlop kell.
  sablonLetolt() {
    this.csvLetolt('lakolista_minta.csv', [
      TarsashazComponent.OSZLOPOK,
      ['1', 'fszt. 1. lakás', 'Kis Péter', 'Kis Péter', '', '1234/10000', '52,4', 'Vízórás', '', 'igen', ''],
      ['2', 'fszt. 2. lakás', 'Nagy Éva', 'Nagy Éva', '', '987/10000', '41,8', 'Vízórás', '', 'igen', ''],
      ['3', 'I. em. 1. lakás', 'Tóth Kft.', 'Tóth Kft.', '', '2500/10000', '106,2', 'Vízóra nélküli', '', 'igen', ''],
      ['4', 'garázs', 'Kis Péter', 'Kis Péter', '', '279/10000', '18,0', 'Alap (besorolás nélkül)', '2500', 'igen', 'fix díjas'],
    ]);
  }

  /*  A JELENLEGI LAKÓLISTA MENTÉSE – ugyanabban a formában, ahogy az import várja, tehát
   *  ez a fájl változtatás nélkül visszatölthető. Minden mező benne van (RLB partner,
   *  díjkategória, fix költség, aktív, megjegyzés), nem csak a minta öt oszlopa – így egy
   *  gépcsere vagy egy elrontott tömeges módosítás után is vissza lehet állni. */
  lakokExport() {
    if (!this.albetetek.length) { this.hiba('Nincs mit exportálni – még nincs lakó felvéve.'); return; }
    const sorok = [TarsashazComponent.OSZLOPOK, ...this.albetetek.map((a) => [
      a.albetet, a.megnevezes, a.tulajdonos, a.rlb_partner, a.rlb_partazon,
      this.csvSzam(a.hanyad), this.csvSzam(a.terulet),
      // A díjkategória NEVÉT írjuk ki („Vízórás"), nem a belső kulcsot: a táblázatot ember
      // olvassa és szerkeszti. A visszatöltés a nevet és a kulcsot is felismeri.
      this.kategoriaNev(a.dij_kategoria || 'alap'),
      this.csvSzam(a.fix_koltseg), a.aktiv ? 'igen' : 'nem', a.megjegyzes,
    ])];
    const ma = new Date();
    const nap = `${ma.getFullYear()}-${String(ma.getMonth() + 1).padStart(2, '0')}-${String(ma.getDate()).padStart(2, '0')}`;
    this.csvLetolt(`lakolista_${nap}.csv`, sorok);
    this.ok(`${this.albetetek.length} lakó exportálva.`);
  }

  /* ── havi kiírás ──────────────────────────────────────────────────────────── */

  async kiirasMent() {
    this.szamitFut = true;
    try {
      const r: any = await this.put(`/${this.aktiv.ugyfelId}/kiiras/${this.ev}/${this.honap}`, this.kiiras);
      this.tetelek = r?.tetelek || [];
      this.osszesen = r?.osszesen || 0;
      this.hianyzoAlap = r?.hianyzo_alap || 0;
      this.ismetlodoJeloles = r?.ismetlodo_jeloles || 0;
      this.ok(`Kiírás kiszámolva: ${this.tetelek.length} albetét, ${this.osszesen.toLocaleString('hu-HU')} Ft.`);
    } catch (e: any) { this.hiba(e?.error?.error || 'A számítás nem sikerült.'); }
    finally { this.szamitFut = false; }
  }

  async rlbExport() {
    this.exportFut = true;
    try {
      const url = `${this.base}/${this.aktiv.ugyfelId}/rlb-export/${this.ev}/${this.honap}`;
      const resp = await firstValueFrom(this.http.get(url, { responseType: 'blob', observe: 'response' }));
      const blob = resp.body as Blob;
      if (!blob || blob.size === 0) { this.hiba('Nincs mit exportálni.'); return; }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `kozos_koltseg_${this.ev}_${String(this.honap).padStart(2, '0')}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      this.ok(`RLB vevőszámla-export kész: ${resp.headers.get('X-Rlb-Tetel') || '?'} számla.`);
    } catch (e: any) {
      let uzenet = 'Az export nem sikerült.';
      try { const szoveg = await (e?.error as Blob)?.text?.(); if (szoveg) uzenet = JSON.parse(szoveg).error || uzenet; } catch { /* marad */ }
      this.hiba(uzenet);
    } finally { this.exportFut = false; }
  }

  async honapValt() { await this.kiirasBetolt(); }

  private async ok(uzenet: string) { (await this.toast.create({ message: uzenet, duration: 2600, color: 'success' })).present(); }
  private async hiba(uzenet: string) { (await this.toast.create({ message: uzenet, duration: 4200, color: 'danger' })).present(); }
}
